import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  Grid,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { api } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { renderTextWithLinks } from "../utils/linkify";

type Gift = {
  id: number;
  title: string;
  description: string;
  totalQuantity: number;
  availableQuantity: number;
  userReservedQuantity: number;
};

type ReservationDialogProps = {
  open: boolean;
  gift: Gift;
  onClose: () => void;
  onReserve: (giftId: number, quantity: number) => void;
  initialQuantity?: number;
};

function ReservationDialog({
  open,
  gift,
  onClose,
  onReserve,
  initialQuantity = 0,
}: ReservationDialogProps) {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(initialQuantity || 1);
  const [error, setError] = useState("");

  useEffect(() => {
    setQuantity(initialQuantity || 1);
  }, [initialQuantity]);

  const handleQuantityChange = (value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      return;
    }

    const newQuantity = parseInt(value) || 0;

    if (newQuantity > gift.availableQuantity + initialQuantity) {
      setError(
        t("gifts.quantity.error_too_many", {
          max: gift.availableQuantity + initialQuantity,
        }),
      );
    } else {
      setError("");
    }

    setQuantity(newQuantity);
  };

  const handleSubmit = () => {
    if (quantity < 1) {
      setError(t("gifts.quantity.error_min"));
      return;
    }

    if (quantity > gift.availableQuantity + initialQuantity) {
      setError(
        t("gifts.quantity.error_too_many", {
          max: gift.availableQuantity + initialQuantity,
        }),
      );
      return;
    }

    onReserve(gift.id, quantity);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ fontFamily: "'Raleway', sans-serif" }}>
        {initialQuantity
          ? t("gifts.update.title", { gift: gift.title })
          : t("gifts.reserve.title", { gift: gift.title })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            type="text"
            fullWidth
            label={t("gifts.reserve.quantity")}
            value={quantity}
            onChange={(e) => handleQuantityChange(e.target.value)}
            error={!!error}
            helperText={
              error ||
              t(
                initialQuantity
                  ? "gifts.quantity.available_edit"
                  : "gifts.quantity.available",
                {
                  count: gift.availableQuantity,
                },
              )
            }
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          onClick={handleSubmit}
          disabled={quantity < 1 || !!error}
          variant="contained"
        >
          {initialQuantity ? t("common.update") : t("gifts.reserve.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function GiftRegistry() {
  const { t } = useTranslation();
  const [selectedGift, setSelectedGift] = useState<Gift | null>(null);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  const fetchGifts = async () => {
    try {
      const data = await api.getGifts();
      setGifts(data);
    } catch (err) {
      console.error("Error fetching gifts:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGifts();
  }, []);

  const handleReserve = async (giftId: number, quantity: number) => {
    if (!currentUser) {
      setError("Please log in to reserve gifts");
      return;
    }

    try {
      await api.reserveGift(giftId, quantity);
      await fetchGifts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reserve gift");
    }
  };

  const handleDelete = async (giftId: number) => {
    if (!currentUser) return;

    try {
      await api.deleteReservation(giftId);
      await fetchGifts();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete reservation",
      );
    }
  };

  // Sort gifts: available first, then fully reserved
  const sortedGifts = [...gifts].sort((a, b) => {
    if (a.userReservedQuantity > 0) return -1;
    if (b.userReservedQuantity > 0) return 1;
    if (a.availableQuantity === 0 && b.availableQuantity > 0) return 1;
    if (b.availableQuantity === 0 && a.availableQuantity > 0) return -1;
    return 0;
  });

  if (loading) return <LinearProgress />;

  return (
    <Container>
      <Box sx={{ py: 8, mt: 4 }}>
        <Typography
          variant="h2"
          sx={{
            textAlign: "center",
            mb: 4,
            fontSize: "3rem",
            fontFamily: "'Great Vibes', cursive",
          }}
        >
          {t("gifts.title")}
        </Typography>

        {error && currentUser && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!currentUser && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Trans i18nKey="gifts.login_to_reserve">
              Vänligen <a href="/login">logga in</a> för att reservera gåvor
            </Trans>
          </Alert>
        )}

        <Box sx={{ mb: 4, textAlign: "center", maxWidth: 800, mx: "auto" }}>
          <Typography
            sx={{
              fontFamily: "'Raleway', sans-serif",
              fontSize: "1.1rem",
              fontStyle: "italic",
            }}
          >
            {currentUser
              ? t("gifts.honeymoon_contribution")
              : t("gifts.honeymoon_contribution_unauthenticated")}
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ maxWidth: 800, mx: "auto" }}>
          {sortedGifts.map((gift) => {
            const totalQuantity = gift.totalQuantity;
            const hasUserReservation =
              currentUser && !!gift.userReservedQuantity;

            return (
              <Grid size={{ xs: 12, sm: 6 }} key={gift.id}>
                <Paper
                  elevation={2}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    width: "100%",
                    height: "100%",
                    fontFamily: "'Raleway', sans-serif",
                    opacity: gift.availableQuantity === 0 ? 0.7 : 1,
                    position: "relative",
                    backgroundColor: hasUserReservation
                      ? "rgba(76, 175, 80, 0.1)"
                      : "inherit",
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "'Raleway', sans-serif",
                        fontWeight: "bold",
                        textDecoration:
                          gift.availableQuantity === 0
                            ? "line-through"
                            : "none",
                      }}
                    >
                      {gift.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Raleway', sans-serif",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {renderTextWithLinks(gift.description)}
                    </Typography>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    flexGrow={1}
                    alignItems="stretch"
                  >
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        100,
                        ((totalQuantity - gift.availableQuantity) /
                          totalQuantity) *
                          100,
                      )}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        flexGrow: 1,
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="row"
                        gap={1}
                        flexGrow={1}
                        justifyContent="space-between"
                        alignItems="center"
                        id="dingdong"
                      >
                        <Typography variant="body2">
                          {t("gifts.quantity.info", {
                            available: gift.availableQuantity,
                            count: totalQuantity,
                          })}
                        </Typography>
                        <Box>
                          {currentUser && hasUserReservation ? (
                            <Box
                              height="46px"
                              display="flex"
                              alignItems="center"
                            >
                              <Tooltip
                                title={t("gifts.your_reservation", {
                                  count: gift.userReservedQuantity ?? 0,
                                })}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => setSelectedGift({ ...gift })}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t("common.delete")}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(gift.id)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={
                                gift.availableQuantity === 0 || !currentUser
                              }
                              onClick={() => setSelectedGift(gift)}
                            >
                              {t("gifts.reserve.button")}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {selectedGift && (
        <ReservationDialog
          open={!!selectedGift}
          gift={selectedGift}
          onClose={() => setSelectedGift(null)}
          onReserve={handleReserve}
          initialQuantity={selectedGift.userReservedQuantity}
        />
      )}
    </Container>
  );
}
